"use client"
import { ScenarioEndNavGrid } from "@/app/playbook/branch-node/ScenarioEndNavGrid"
import { PlanATimelineDiagramStandalone } from "@/app/playbook/branch-node/plan-a-timeline-diagram/PlanATimelineDiagramStandalone"
import { SCENARIO_END_HUB_HEADING_ID } from "@/app/playbook/branch-node/plan-a-timeline-diagram/planATimelineDiagramModel"

// The "end of the scenario" cap shared by every finishing point:
// Plan A's epilogue and each standalone perspective document. All show
// the same things — a "you've reached the end" heading, the timeline
// diagram, and the 2×2 nav grid — so they live together here, only the
// heading text varies per ending.

// Which ending this footer caps. Each value selects the heading text
// below; the nav grid is the same for all.
// The value is purely the heading the reader lands under — NOT "which
// document mounted me" — so a new mount point picks the ending whose
// copy fits (e.g. the inline Plan A continuation and the main Plan A
// path both end on the SCENARIO, so both use "mainScenario"; only the
// epilogue uses "epilogue").
export type ScenarioEnding = "mainScenario" | "epilogue" | "public" | "insider"

// Heading shown at the top of the end cap, one per ending. Edit copy
// here. Single source of truth for the "you've reached the end of …"
// line — every cap reads it, so copy can't drift between mount points.
export const SCENARIO_END_HEADINGS: Record<ScenarioEnding, string> = {
  mainScenario: "You've reached the end of Plan A",
  epilogue: "You've reached the end of the Epilogue",
  public: "You've reached the end of the Public's Perspective",
  insider: "You've reached the end of the Insider's Perspective",
}

// Full end-of-scenario cap: heading + timeline diagram + 2×2 nav grid.
// Used at the end of the epilogue and each standalone
// perspective document, landing every ending on the same nav hub.
//
// `inlinePlanAContinuation` is set only by ContinueWithPlan, where this
// cap sits above the INLINE Plan A epilogue (after finishing another
// plan). It tells the nav grid its surrounding content is already Plan A,
// so the Epilogue button expands that inline epilogue in place rather
// than jumping to the real Plan A path. Every other caller leaves it
// false (normal hash-driven navigation).
export const ScenarioEndFooter = ({
  ending,
  inlinePlanAContinuation = false,
}: {
  ending: ScenarioEnding
  inlinePlanAContinuation?: boolean
}) => (
  <section className="my-0 rounded-lg">
    {/* Only the HUB instance (mainScenario — the cap the ending bodies expand
        under) carries the scroll-frame id; the epilogue/POV end caps render
        their own copies of this heading and must not steal the anchor. */}
    <h2
      id={ending === "mainScenario" ? SCENARIO_END_HUB_HEADING_ID : undefined}
      className="m-0 mt-12 mb-4 text-2xl md:text-4xl font-bold leading-tight"
    >
      {SCENARIO_END_HEADINGS[ending]}
    </h2>

    {/* The original branching timeline diagram, driving the same
        hash-based navigation as the nav grid below it. */}
    <div className="mb-8">
      <PlanATimelineDiagramStandalone inlinePlanAContinuation={inlinePlanAContinuation} />
    </div>

    <div className="text-xs uppercase tracking-widest font-mono text-black/70 font-bold mb-4">
      Keep exploring
    </div>

    {/* Like the hub heading id above, only the HUB instance carries the
        nav-grid marker: useGalaxy measures it to decide whether the Epilogue
        button (the one the reader clicked) is still on/above screen, which
        keeps the HUD in epilogue mode. The epilogue/POV end caps render their
        own copies of this grid and must not steal the measurement. */}
    <div data-epilogue-nav-grid={ending === "mainScenario" ? "true" : undefined}>
      <ScenarioEndNavGrid inlinePlanAContinuation={inlinePlanAContinuation} />
    </div>
  </section>
)
